import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  volunteerApi,
  protectedVolunteerApi,
  userApi,
  adminApi,
  type CreateVolunteerDto,
  type VolunteerResponse,
  type CreateUserDto,
  type UpdateProfileDto,
} from '../api/volunteer';

// Query keys
const QUERY_KEYS = {
  volunteers: ['volunteers'] as const,
  volunteer: (id: string) => ['volunteers', id] as const,
  pendingUsers: ['pendingUsers'] as const,
  pendingHealthCertificates: ['pendingHealthCertificates'] as const,
};

/**
 * Hook to sign volunteer contract (public - used in registration flow)
 */
export const useSignVolunteerContract = () => {
  return useMutation({
    mutationFn: ({
      id,
      signatureDataBase64,
    }: {
      id: string;
      signatureDataBase64: string;
    }) => volunteerApi.signContract(id, signatureDataBase64),
  });
};

/**
 * Hook to create user account (public - used in registration flow)
 */
export const useCreateUser = () => {
  return useMutation({
    mutationFn: (data: CreateUserDto) => userApi.create(data),
  });
};

// ============================================
// Protected hooks - require authentication
// Use only in /app protected routes
// ============================================

/**
 * Hook to fetch all volunteers (protected - requires auth)
 */
export const useVolunteers = (enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.volunteers,
    queryFn: protectedVolunteerApi.getAll,
    enabled,
  });
};

/**
 * Hook to fetch a single volunteer (protected - requires auth)
 */
export const useVolunteer = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.volunteer(id),
    queryFn: () => protectedVolunteerApi.getById(id),
    enabled: !!id,
  });
};

/**
 * Hook to update a volunteer (protected - requires auth)
 */
export const useUpdateVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateVolunteerDto>;
    }) => protectedVolunteerApi.update(id, data),
    onSuccess: (data: VolunteerResponse) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
      queryClient.setQueryData(QUERY_KEYS.volunteer(data.id), data);
    },
  });
};

/**
 * Hook to delete a volunteer (protected - requires auth)
 */
export const useDeleteVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: protectedVolunteerApi.delete,
    onSuccess: (_, deletedId: string) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.volunteer(deletedId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};

/**
 * Hook to update own user profile (protected - authenticated users only)
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileDto) => userApi.updateOwnProfile(data),
    onSuccess: () => {
      // Invalidate volunteer queries to refresh user data if needed
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};

export const useUploadOwnHealthCertificate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => userApi.uploadOwnHealthCertificate(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};

export const useDeleteOwnHealthCertificate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.deleteOwnHealthCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};

// ============================================
// Admin hooks - require admin permissions
// Use only in admin pages
// ============================================

/**
 * Hook to fetch all pending users (admin only)
 */
export const usePendingUsers = (enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.pendingUsers,
    queryFn: adminApi.getPendingUsers,
    enabled,
  });
};

export const usePendingHealthCertificates = (enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.pendingHealthCertificates,
    queryFn: adminApi.getPendingHealthCertificates,
    enabled,
  });
};

export const useApproveHealthCertificate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminApi.approveHealthCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.pendingHealthCertificates,
      });
    },
  });
};

export const useRejectHealthCertificate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminApi.rejectHealthCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.pendingHealthCertificates,
      });
    },
  });
};

/**
 * Hook to approve a pending user (admin only)
 */
export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminApi.approveUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingUsers });
    },
  });
};

/**
 * Hook to reject a pending user (admin only)
 */
export const useRejectUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminApi.rejectUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingUsers });
    },
  });
};

export const useDeleteApprovedVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminApi.deleteApprovedVolunteer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};

const useAdminDocumentUpload = (
  upload: (volunteerId: string, file: File) => Promise<void>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ volunteerId, file }: { volunteerId: string; file: File }) =>
      upload(volunteerId, file),
    onSuccess: (_, { volunteerId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.volunteer(volunteerId),
      });
    },
  });
};

export const useUploadVolunteerContract = () =>
  useAdminDocumentUpload(adminApi.uploadVolunteerContract);

export const useUploadVolunteerHealthCertificate = () =>
  useAdminDocumentUpload(adminApi.uploadVolunteerHealthCertificate);

/**
 * Hook to update a user's role (admin only)
 */
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'SUPER_ADMIN' | 'ADMIN' | 'VOLUNTEER';
    }) => adminApi.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.volunteers });
    },
  });
};
